import { useState } from "react";
import PdsAdsDetailModal from "./PdsAdsDetailModal";

/**
 * NapPdsMatchModal — disconnected (PDS/ADS) subscribers on the same street +
 * barangay + municipality as an installed NAP subscriber. Each row opens the
 * full PDS/ADS record detail.
 *
 * Props:
 *   subscriberName - the installed subscriber that was clicked (header)
 *   loading        - true while fetching
 *   data           - API response { municipality, barangay, street, street_matched, count, rows }
 *   onClose        - close the modal
 */

function TypeBadge({ type }) {
  let cls = "bg-gray-100 text-gray-700";
  if (type === "PDS") cls = "bg-red-100 text-red-700";
  else if (type === "ADS") cls = "bg-amber-100 text-amber-700";
  return <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${cls}`}>{type || "—"}</span>;
}

export default function NapPdsMatchModal({ subscriberName, loading, data, onClose }) {
  const rows = data?.rows || [];
  const [detailId, setDetailId] = useState(null);

  return (
    <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Disconnected Subscribers Nearby (PDS/ADS)</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Near <span className="font-semibold text-gray-700">{subscriberName}</span> — potential returning subscribers
            </p>
            {data && (
              <p className="text-xs text-gray-500 mt-1">
                {data.street_matched ? (
                  <>Street <span className="font-medium text-gray-700">“{data.street}”</span> · </>
                ) : (
                  <span className="text-amber-600">No street detected — showing whole barangay · </span>
                )}
                {data.barangay}, {data.municipality}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="overflow-auto">
          {loading ? (
            <div className="px-5 py-12 text-center text-gray-500">Searching PDS/ADS records…</div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center text-gray-400">
              No disconnected subscribers found on this street/barangay.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 w-20">Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[180px]">Subscriber Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-44">Phone</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-32">Play Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-40">Service Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[240px]">Address</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr
                    key={r.id}
                    onClick={() => setDetailId(r.id)}
                    title="View full record"
                    className={`cursor-pointer hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                  >
                    <td className="px-3 py-2 text-center align-top"><TypeBadge type={r.disconnect_type} /></td>
                    <td className="px-3 py-2 align-top font-medium text-blue-700 underline decoration-dotted">
                      {r.name || "—"}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.phone || "—"}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.play_type || "—"}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.service_type || "—"}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between">
          <span className="text-sm text-gray-500">{loading ? "" : `${rows.length} record(s)`}</span>
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-1.5 rounded">
            Close
          </button>
        </div>
      </div>

      {detailId && <PdsAdsDetailModal recordId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
